import { QuestionCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Col, Descriptions, Row, Tooltip, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, MosaicComponent, getPointPermission } from '@/components'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { ResourceOKR } from '@/components/resource-okr.component'
import { NormalResourceList } from '@/modules/portfolio/portfolio-list'
import { ResourceReport } from '@/modules/project/project-detail/project-summary'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType, ResourceUser } from '@/schemas'

import { Devlop, DevlopClassification } from '../../devlop.schema'

const { Title } = Typography

interface DevlopSummaryContainerProps {
  devlop: Devlop
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

export const DevlopSummaryContainer: React.FC<DevlopSummaryContainerProps> = ({ devlop, setUsers }) => {
  const { t } = useTranslation()
  const devlopId = devlop.selfId

  const { run: updateDevlop } = useRequest(
    (data) => ({
      url: `/api/devlop/patch/${devlopId}`,
      method: 'patch',
      data: {
        ...data,
      },
    }),
    {
      manual: true,
      debounceInterval: 500,
      fetchKey: (params) => params.field,
    }
  )

  return (
    <Row gutter={[32, 0]}>
      <Col xs={24} md={16} className='children:mb-24px'>
        <Descriptions size='small' className='mt-8px'>
          <Descriptions.Item
            label={
              <span>
                {t('devlop.detail.priority')}
                <PriorityPopoverDescription />
              </span>
            }
          >
            {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, devlop.permissions).canRead ? (
              t(`form.priority.${PriorityEnum[devlop.priority]}`)
            ) : (
              <MosaicComponent />
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('devlop.detail.classification')}>
            {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_产品分类, devlop.permissions).canRead ? (
              t(DevlopClassification[devlop.classification])
            ) : (
              <MosaicComponent />
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('devlop.timePeriod')}>
            {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, devlop.permissions).canRead ? (
              devlop.startDate && devlop.endDate ? (
                moment(devlop.startDate).utc().format('YYYY-MM-DD') +
                ' - ' +
                moment(devlop.endDate).utc().format('YYYY-MM-DD')
              ) : (
                'N/A'
              )
            ) : (
              <MosaicComponent />
            )}
          </Descriptions.Item>
        </Descriptions>
        <div>
          <Title className='mt-3' level={5}>
            {t('devlop.detail.background')}
            <Tooltip title={t('devlop.form.background.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>
          <Editor
            editorKey={devlop.selfId}
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], devlop.permissions).canUpdate}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], devlop.permissions).canRead}
            content={devlop.background}
            onSave={(background) => updateDevlop({ background })}
          />
          <br />
          <Title className='mt-3' level={5}>
            {t('devlop.detail.goal')}
            <Tooltip title={t('devlop.form.goal.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>
          <Editor
            editorKey={devlop.selfId}
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, devlop.permissions).canUpdate}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, devlop.permissions).canRead}
            content={devlop.goal}
            onSave={(goal) => updateDevlop({ goal })}
          />
          <br />
          <Title className='mt-3' level={5}>
            {t('devlop.detail.standard')}
            <Tooltip title={t('devlop.form.standard.tooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>
          <Editor
            editorKey={devlop.selfId}
            editable={
              getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], devlop.permissions).canUpdate
            }
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], devlop.permissions).canRead}
            content={devlop.standard}
            onSave={(standard) => updateDevlop({ standard })}
          />
          <br />
          <Title className='mt-3' level={5}>
            {t('devlop.detail.challenge')}
            <Tooltip title={t('resource.challengeTooltip')}>
              <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
            </Tooltip>
          </Title>
          <Editor
            editorKey={devlop.selfId}
            editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, devlop.permissions).canUpdate}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, devlop.permissions).canRead}
            content={devlop.challenge}
            onSave={(challenge) => updateDevlop({ challenge })}
          />
          <br />
        </div>

        <ResourceOKR resource={devlop} isSummaryPage={true} />
        <ResourceUsers resourceType={ResourceType.DEVLOP} source={devlop} setUsers={setUsers} />
      </Col>

      <Col xs={24} md={8}>
        <ResourceReport resource={devlop} />
      </Col>

      <Col span={24}>
        <NormalResourceList resource={devlop} pageType={ResourceType.DEVLOP} />
      </Col>
    </Row>
  )
}
